import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, collection, onSnapshot, query, orderBy, handleFirestoreError, OperationType, addDoc, updateDoc, doc, deleteDoc, Timestamp, where, getDocs } from '../firebase';
import { handleAsyncAction } from '../utils/AsyncActionHandler';
import { Client, SparePart, Task, Report, Branch, GeneralServiceItem } from '../types';
import { useAuth } from '../App';
import { read, utils, write } from 'xlsx';
import { FilterBar, FilterOption } from '../components/FilterBar';
import { 
  Plus, 
  Search, 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  MoreVertical, 
  Trash2, 
  Edit2,
  X,
  CheckCircle2,
  Package,
  Calendar,
  Clock,
  FileText,
  DollarSign,
  ChevronRight,
  Loader2,
  Filter,
  User as UserIcon,
  Download,
  ListTodo,
  Activity,
  ExternalLink,
  FileUp,
  Printer
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import ConfirmationModal from '../components/ConfirmationModal';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const getContractStatusBadge = (endDateStr?: string) => {
  if (!endDateStr) {
    return (
      <span className="px-2 py-0.5 bg-slate-50 text-slate-400 text-[10px] font-black uppercase tracking-wider rounded-md border border-slate-100">
        No Contract
      </span>
    );
  }
  const now = new Date();
  const end = new Date(endDateStr + 'T23:59:59');
  const diffMs = end.getTime() - now.getTime();
  
  if (diffMs <= 0) {
    return (
      <span className="px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-wider rounded-md border border-red-100 animate-pulse">
        Expired
      </span>
    );
  }
  
  const daysRemaining = Math.ceil(diffMs / (1000 * 3600 * 24));
  if (daysRemaining <= 90) { // expiring soon: under 3 months
    return (
      <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-black uppercase tracking-wider rounded-md border border-amber-100">
        Expiring Soon ({daysRemaining}d)
      </span>
    );
  }
  
  return (
    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-wider rounded-md border border-emerald-100">
      Active
    </span>
  );
};

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [allServiceTypes, setAllServiceTypes] = useState<any[]>([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'service_types'), (snapshot) => {
      const types: any[] = [];
      snapshot.forEach((doc) => {
        types.push({ id: doc.id, ...doc.data() });
      });
      setAllServiceTypes(types);
    });
    return () => unsubscribe();
  }, []);

  const filterOptions: FilterOption[] = [
    {
      key: 'service',
      label: 'All Services',
      type: 'select',
      options: allServiceTypes.map(t => ({ value: t.name, label: t.name }))
    }
  ];

  const handleFilterChange = (key: string, value: any) => {
    if (key === 'service') setServiceFilter(value);
  };
// ...
  const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
  const [isSparePartsModalOpen, setIsSparePartsModalOpen] = useState(false);
  const [editingPartIndex, setEditingPartIndex] = useState<number | null>(null);
  const [branchName, setBranchName] = useState('');
  const [branchAddress, setBranchAddress] = useState('');
  const [branchAcQuantity, setBranchAcQuantity] = useState<number | ''>('');
  const [branchSearch, setBranchSearch] = useState('');
  const [sparePartSearch, setSparePartSearch] = useState('');
  const [contractHistorySearch, setContractHistorySearch] = useState('');
  const [selectedSpareParts, setSelectedSpareParts] = useState<number[]>([]);
  const [newPart, setNewPart] = useState({ name: '', price: '', unit: '' });
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);
  const { user, profile, isAdmin, isManager } = useAuth();

  // Form state
  const [clientFormData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    serviceTypes: [] as string[],
    schedule: '',
    contractDuration: '',
    currency: 'TZS',
    contractCommencementDate: '',
    contractEndDate: '',
    branches: [] as { name: string; address: string; acQuantity?: number }[]
  });
  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [generalServiceItems, setGeneralServiceItems] = useState<GeneralServiceItem[]>([]);
  const [newGeneralServiceItem, setNewGeneralServiceItem] = useState({ description: '', quantity: '', unitPrice: '' });
  const [viewingClient, setViewingClient] = useState<Client | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [contractHistory, setContractHistory] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'reports' | 'branches' | 'spare-parts' | 'general-service' | 'contract-history' | 'activity'>('overview');

  const addGeneralServiceItem = () => {
    if (newGeneralServiceItem.description && newGeneralServiceItem.quantity && newGeneralServiceItem.unitPrice) {
      setGeneralServiceItems([...generalServiceItems, {
        description: newGeneralServiceItem.description,
        quantity: parseInt(newGeneralServiceItem.quantity) || 1,
        unitPrice: parseFloat(newGeneralServiceItem.unitPrice) || 0
      }]);
      setNewGeneralServiceItem({ description: '', quantity: '', unitPrice: '' });
    }
  };

  const removeGeneralServiceItem = (index: number) => {
    setGeneralServiceItems(generalServiceItems.filter((_, i) => i !== index));
  };
  const [notes, setNotes] = useState<any[]>([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState('TZS');

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    isDanger?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (viewingClient) {
      const tasksQuery = query(collection(db, 'tasks'), where('clientId', '==', viewingClient.id));
      const reportsQuery = query(collection(db, 'reports'), where('clientId', '==', viewingClient.id));
      const branchesQuery = collection(db, 'clients', viewingClient.id, 'branches');
      const historyQuery = query(collection(db, 'clients', viewingClient.id, 'contractHistory'), orderBy('changedAt', 'desc'));
      const notesQuery = query(collection(db, 'clients', viewingClient.id, 'notes'), orderBy('createdAt', 'desc'));
      
      const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
        setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task)));
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'tasks');
      });
      const unsubscribeReports = onSnapshot(reportsQuery, (snapshot) => {
        setReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, 'reports');
      });
      const unsubscribeBranches = onSnapshot(branchesQuery, (snapshot) => {
        setBranches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Branch)));
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, `clients/${viewingClient.id}/branches`);
      });
      const unsubscribeHistory = onSnapshot(historyQuery, (snapshot) => {
        setContractHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error('Error fetching contract history:', error);
      });
      const unsubscribeNotes = onSnapshot(notesQuery, (snapshot) => {
        setNotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error('Error fetching client notes:', error);
      });
      
      return () => {
        unsubscribeTasks();
        unsubscribeReports();
        unsubscribeBranches();
        unsubscribeHistory();
        unsubscribeNotes();
      };
    }
  }, [viewingClient]);

  const addBranch = async () => {
    if (branchName && viewingClient) {
      await handleAsyncAction(async () => {
        await addDoc(collection(db, 'clients', viewingClient.id, 'branches'), {
          name: branchName,
          address: branchAddress,
          acQuantity: Number(branchAcQuantity) || 0,
          createdAt: Timestamp.now()
        });
        setIsBranchModalOpen(false);
        setBranchName('');
        setBranchAddress('');
        setBranchAcQuantity('');
      }, setIsSubmitting, {
        successMessage: 'Branch added successfully',
        errorMessage: 'Failed to add branch',
        onError: (error) => {
          handleFirestoreError(error, OperationType.CREATE, `clients/${viewingClient.id}/branches`);
        }
      });
    }
  };

  const handleDownloadBranchTemplate = () => {
    const headers = ['Branch Name', 'Branch Address', 'AC Quantity'];
    const data = [
      ['Arusha Main Branch', 'Plot 12, Sokoine Road, Arusha', 15],
      ['Dar es Salaam Branch', 'Samora Avenue, Dar es Salaam', 25]
    ];
    const ws = utils.aoa_to_sheet([headers, ...data]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Branch Template');
    const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    downloadFile(blob, 'branch_import_template.xlsx');
    toast.success('Branch import template downloaded successfully');
  };

  const handleImportBranchesExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = utils.sheet_to_json<Record<string, any>>(firstSheet);

      if (!rows || rows.length === 0) {
        toast.error('The selected Excel file is empty.');
        return;
      }

      const importedBranches: { name: string; address?: string; acQuantity?: number }[] = [];

      rows.forEach(row => {
        const nameKey = Object.keys(row).find(k => 
          k.toLowerCase().includes('branch') || k.toLowerCase().includes('name') || k.toLowerCase().includes('location')
        ) || Object.keys(row)[0];

        const addrKey = Object.keys(row).find(k => 
          k.toLowerCase().includes('address') || k.toLowerCase().includes('street') || k.toLowerCase().includes('city')
        );

        const acKey = Object.keys(row).find(k => 
          k.toLowerCase().includes('ac') || k.toLowerCase().includes('qty') || k.toLowerCase().includes('quantity') || k.toLowerCase().includes('count')
        );

        const bName = row[nameKey] ? String(row[nameKey]).trim() : '';
        if (bName) {
          const bAddr = addrKey && row[addrKey] ? String(row[addrKey]).trim() : '';
          const bAc = acKey && row[acKey] ? Number(row[acKey]) || 0 : 0;
          importedBranches.push({
            name: bName,
            address: bAddr,
            acQuantity: bAc
          });
        }
      });

      if (importedBranches.length === 0) {
        toast.error('Could not find any valid branch names in the uploaded sheet.');
        return;
      }

      if (viewingClient) {
        setIsSubmitting(true);
        for (const b of importedBranches) {
          await addDoc(collection(db, 'clients', viewingClient.id, 'branches'), {
            ...b,
            createdAt: Timestamp.now()
          });
        }
        setIsSubmitting(false);
        toast.success(`Successfully imported ${importedBranches.length} branch(es) for ${viewingClient.name}!`);
      } else {
        setFormData(prev => ({
          ...prev,
          branches: [
            ...(prev.branches || []),
            ...importedBranches.map(b => ({ name: b.name, address: b.address || '', acQuantity: (b as any).acQuantity || 0 }))
          ]
        }));
        toast.success(`Loaded ${importedBranches.length} branch(es) into client form!`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to parse Excel file.');
    } finally {
      e.target.value = '';
    }
  };

  const deleteBranch = async (branchId: string) => {
    if (viewingClient) {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Branch',
        message: 'Are you sure you want to delete this branch? This action cannot be undone.',
        onConfirm: async () => {
          try {
            await deleteDoc(doc(db, 'clients', viewingClient.id, 'branches', branchId));
            toast.success('Branch deleted successfully');
          } catch (error) {
            handleFirestoreError(error, OperationType.DELETE, `clients/${viewingClient.id}/branches/${branchId}`);
            toast.error('Failed to delete branch');
          }
        },
        isDanger: true,
      });
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim() || !viewingClient) return;
    setIsAddingNote(true);
    try {
      await addDoc(collection(db, 'clients', viewingClient.id, 'notes'), {
        text: newNoteText.trim(),
        createdAt: Timestamp.now(),
        createdBy: profile?.displayName || user?.email || 'System',
        createdByEmail: user?.email || ''
      });
      setNewNoteText('');
      toast.success('Activity note logged successfully');
    } catch (err) {
      console.error('Error adding activity note:', err);
      toast.error('Failed to log activity note');
    } finally {
      setIsAddingNote(false);
    }
  };

  const saveSparePartPrice = async () => {
    if (newPart.name && newPart.price !== '' && viewingClient) {
      await handleAsyncAction(async () => {
        let updatedSpareParts = [...(viewingClient.spareParts || [])];
        const parsedPrice = parseFloat(newPart.price) || 0;
        if (editingPartIndex !== null) {
          updatedSpareParts[editingPartIndex] = { 
            name: newPart.name, 
            price: parsedPrice,
            unit: newPart.unit
          };
        } else {
          updatedSpareParts.push({ 
            name: newPart.name, 
            price: parsedPrice,
            unit: newPart.unit
          });
        }
        
        // Optimistically update viewing client state and close modal immediately
        setViewingClient({ ...viewingClient, spareParts: updatedSpareParts });
        setIsSparePartsModalOpen(false);
        setNewPart({ name: '', price: '', unit: '' });
        setEditingPartIndex(null);

        try {
          await updateDoc(doc(db, 'clients', viewingClient.id), {
            spareParts: updatedSpareParts
          });
        } catch (err) {
          console.error('Error updating spare parts in Firestore:', err);
        }
      }, setIsSubmitting, {
        successMessage: `Spare part ${editingPartIndex !== null ? 'updated' : 'added'} successfully`,
        errorMessage: `Failed to ${editingPartIndex !== null ? 'update' : 'add'} spare part price`,
        timeoutMs: 4000,
        onError: (error) => {
          handleFirestoreError(error, OperationType.UPDATE, `clients/${viewingClient.id}`);
        }
      });
    } else {
      toast.error('Please enter Part Name and Price');
    }
  };

  const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !viewingClient) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result;
        const wb = read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = utils.sheet_to_json(ws) as any[];

        const importedParts: SparePart[] = data.map(row => {
          const name = row.name || row.Name || row['Part Name'] || row.part || row.Part || row.description || row.Description || '';
          const priceStr = row.price || row.Price || row.amount || row.Amount || row.unitPrice || row.UnitPrice || '0';
          const price = typeof priceStr === 'number' ? priceStr : parseFloat(priceStr.toString().replace(/[^0-9.]/g, ''));
          const unit = row.unit || row.Unit || row.Measure || row.measure || '';
          
          return {
            name: name.toString(),
            price: isNaN(price) ? 0 : price,
            unit: unit.toString()
          };
        }).filter(part => part.name !== '');

        if (importedParts.length === 0) {
          toast.error('No valid spare parts found in the file. Ensure columns are named "Name" and "Price".');
          return;
        }

        const updatedSpareParts = [...(viewingClient.spareParts || []), ...importedParts];
        
        await updateDoc(doc(db, 'clients', viewingClient.id), {
          spareParts: updatedSpareParts
        });

        setViewingClient({ ...viewingClient, spareParts: updatedSpareParts });
        toast.success(`Successfully imported ${importedParts.length} spare parts`);
      } catch (error) {
        console.error('Error importing Excel:', error);
        toast.error('Failed to import Excel file. Please check the format.');
      }
    };
    reader.readAsBinaryString(file);
    // Reset input
    e.target.value = '';
  };

  const deleteSparePart = async (index: number) => {
    if (viewingClient && viewingClient.spareParts) {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Spare Part',
        message: 'Are you sure you want to delete this spare part? This action cannot be undone.',
        onConfirm: async () => {
          try {
            const updatedSpareParts = viewingClient.spareParts!.filter((_, i) => i !== index);
            await updateDoc(doc(db, 'clients', viewingClient.id), {
              spareParts: updatedSpareParts
            });
            setViewingClient({ ...viewingClient, spareParts: updatedSpareParts });
            setSelectedSpareParts(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
            toast.success('Spare part deleted successfully');
          } catch (error) {
            handleFirestoreError(error, OperationType.UPDATE, `clients/${viewingClient.id}`);
            toast.error('Failed to delete spare part');
          }
        },
        isDanger: true,
      });
    }
  };

  const deleteSelectedSpareParts = async () => {
    if (viewingClient && viewingClient.spareParts && selectedSpareParts.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: 'Delete Selected Parts',
        message: `Are you sure you want to delete ${selectedSpareParts.length} selected spare parts? This action cannot be undone.`,
        onConfirm: async () => {
          try {
            const updatedSpareParts = viewingClient.spareParts!.filter((_, index) => !selectedSpareParts.includes(index));
            await updateDoc(doc(db, 'clients', viewingClient.id), {
              spareParts: updatedSpareParts
            });
            setViewingClient({ ...viewingClient, spareParts: updatedSpareParts });
            setSelectedSpareParts([]);
            toast.success(`${selectedSpareParts.length} spare parts deleted successfully`);
          } catch (error) {
            handleFirestoreError(error, OperationType.UPDATE, `clients/${viewingClient.id}`);
            toast.error('Failed to delete selected spare parts');
          }
        },
        isDanger: true,
      });
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'clients'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
      setClients(clientsData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    const settingsUnsubscribe = onSnapshot(collection(db, 'settings'), (snapshot) => {
      if (!snapshot.empty) {
        const settings = snapshot.docs[0].data() as any;
        setDefaultCurrency(settings.defaultCurrency || 'TZS');
        setLetterheadUrl(settings.letterheadUrl || null);
      }
    });

    return () => {
      unsubscribe();
      settingsUnsubscribe();
    };
  }, []);

  // Sync viewingClient when clients list updates to prevent stale data
  useEffect(() => {
    if (viewingClient) {
      const updated = clients.find(c => c.id === viewingClient.id);
      if (updated) {
        // Only update if data changed to avoid unnecessary re-renders
        if (JSON.stringify(updated.spareParts) !== JSON.stringify(viewingClient.spareParts)) {
          setViewingClient(updated);
        }
      } else {
        // Client might have been deleted elsewhere
        setViewingClient(null);
      }
    }
  }, [clients, viewingClient?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleAsyncAction(async () => {
      const data = {
        name: clientFormData.name,
        contactPerson: clientFormData.contactPerson,
        email: clientFormData.email,
        phone: clientFormData.phone,
        address: clientFormData.address,
        serviceType: clientFormData.serviceTypes.join(', '),
        serviceTypes: clientFormData.serviceTypes,
        schedule: clientFormData.schedule,
        contractDuration: clientFormData.contractDuration,
        currency: clientFormData.currency || 'TZS',
        contractCommencementDate: clientFormData.contractCommencementDate || '',
        contractEndDate: clientFormData.contractEndDate || '',
        spareParts,
        generalServiceItems,
        updatedAt: Timestamp.now()
      };

      let clientId = editingClient?.id;

      if (editingClient) {
        const datesChanged = 
          editingClient.contractCommencementDate !== data.contractCommencementDate ||
          editingClient.contractEndDate !== data.contractEndDate ||
          editingClient.contractDuration !== data.contractDuration;

        if (datesChanged && (editingClient.contractCommencementDate || editingClient.contractEndDate)) {
          try {
            await addDoc(collection(db, 'clients', editingClient.id, 'contractHistory'), {
              contractCommencementDate: editingClient.contractCommencementDate || '',
              contractEndDate: editingClient.contractEndDate || '',
              contractDuration: editingClient.contractDuration || '',
              serviceType: editingClient.serviceType || '',
              changedAt: Timestamp.now(),
              changedBy: profile?.displayName || user?.email || 'System'
            });
          } catch (historyErr) {
            console.error('Error logging contract history:', historyErr);
          }
        }

        await updateDoc(doc(db, 'clients', editingClient.id), data);
      } else {
        const docRef = await addDoc(collection(db, 'clients'), {
          ...data,
          createdAt: Timestamp.now()
        });
        clientId = docRef.id;
      }

      // Handle branches
      if (clientId && clientFormData.branches.length > 0) {
        for (const branch of clientFormData.branches) {
          await addDoc(collection(db, 'clients', clientId, 'branches'), {
            ...branch,
            createdAt: Timestamp.now()
          });
        }
      }

      closeModal();
    }, setIsSubmitting, {
      successMessage: editingClient ? 'Client updated successfully' : 'Client created successfully',
      errorMessage: 'Failed to save client data',
      onError: (error) => {
        handleFirestoreError(error, editingClient ? OperationType.UPDATE : OperationType.CREATE, 'clients');
      }
    });
  };

  const parseCSVLine = (line: string) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result.map(v => v.replace(/^"|"$/g, ''));
  };

  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];
    
    const headers = parseCSVLine(lines[0]);
    const result = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const values = parseCSVLine(lines[i]);
      const obj: Record<string, string> = {};
      headers.forEach((header, index) => {
        obj[header.trim().toLowerCase()] = values[index] ? values[index].trim() : '';
      });
      result.push(obj);
    }
    return result;
  };

  const handleImportClientsCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const text = evt.target?.result as string;
        const parsed = parseCSV(text);
        
        if (parsed.length === 0) {
          toast.error('No data found in the CSV file.');
          return;
        }

        let importedCount = 0;
        let skippedCount = 0;

        for (const row of parsed) {
          const name = row['name'] || row['client name'] || row['client'] || '';
          if (!name) {
            skippedCount++;
            continue;
          }

          const contactPerson = row['contact person'] || row['contactperson'] || row['contact'] || '';
          const email = row['email'] || '';
          const phone = row['phone'] || '';
          const address = row['address'] || '';
          const serviceType = row['service type'] || row['servicetype'] || '';
          const schedule = row['schedule'] || '';
          const contractDuration = row['contract duration'] || row['contractduration'] || row['duration'] || '';
          const currency = row['currency'] || 'TZS';
          const contractCommencementDate = row['commencement date'] || row['contractcommencementdate'] || row['commencement'] || '';
          const contractEndDate = row['expiry date'] || row['contractenddate'] || row['expiry'] || row['end date'] || row['enddate'] || '';

          const data = {
            name,
            contactPerson,
            email,
            phone,
            address,
            serviceType: serviceType,
            schedule,
            contractDuration,
            currency,
            contractCommencementDate,
            contractEndDate,
            spareParts: [],
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          };

          await addDoc(collection(db, 'clients'), data);
          importedCount++;
        }

        toast.success(`Bulk upload completed! Imported ${importedCount} clients, skipped ${skippedCount} missing name.`);
      } catch (error) {
        console.error('Error importing CSV:', error);
        toast.error('Failed to import CSV. Please ensure the file has a "name" header and correct format.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const addSparePart = () => {
    if (newPart.name && newPart.price) {
      setSpareParts([...spareParts, { 
        name: newPart.name, 
        price: parseFloat(newPart.price),
        unit: newPart.unit
      }]);
      setNewPart({ name: '', price: '', unit: '' });
    }
  };

  const removeSparePart = (index: number) => {
    setSpareParts(spareParts.filter((_, i) => i !== index));
  };

  const handleDeleteClient = async (clientId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Client Profile',
      message: 'Are you sure you want to delete this client? This will also permanently remove all associated branches. This action cannot be undone.',
      onConfirm: async () => {
        try {
          // Delete branches subcollection first
          const branchesRef = collection(db, 'clients', clientId, 'branches');
          const branchesSnap = await getDocs(branchesRef);
          for (const branchDoc of branchesSnap.docs) {
            await deleteDoc(doc(db, 'clients', clientId, 'branches', branchDoc.id));
          }

          await deleteDoc(doc(db, 'clients', clientId));
          toast.success('Client and associated branches deleted successfully');
          if (viewingClient?.id === clientId) {
            setViewingClient(null);
          }
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `clients/${clientId}`);
          toast.error('Failed to delete client');
        }
      },
      isDanger: true,
    });
  };

  const openModal = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      
      // Handle legacy comma-separated values or single string fallback
      let initialTypes: string[] = [];
      if (client.serviceTypes && Array.isArray(client.serviceTypes)) {
        initialTypes = client.serviceTypes;
      } else if (client.serviceType) {
        initialTypes = client.serviceType.split(',').map(s => s.trim()).filter(Boolean);
      }

      setFormData({
        name: client.name,
        contactPerson: client.contactPerson || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        serviceTypes: initialTypes,
        schedule: client.schedule || '',
        contractDuration: client.contractDuration || '',
        currency: client.currency || 'TZS',
        contractCommencementDate: client.contractCommencementDate || '',
        contractEndDate: client.contractEndDate || '',
        branches: []
      });
      setSpareParts(client.spareParts || []);
      setGeneralServiceItems(client.generalServiceItems || []);
    } else {
      setEditingClient(null);
      setFormData({ 
        name: '', 
        contactPerson: '', 
        email: '', 
        phone: '', 
        address: '',
        serviceTypes: [],
        schedule: '',
        contractDuration: '',
        currency: defaultCurrency,
        contractCommencementDate: '',
        contractEndDate: '',
        branches: []
      });
      setSpareParts([]);
      setGeneralServiceItems([]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Header
    if (letterheadUrl) {
      try {
        doc.addImage(letterheadUrl, 'PNG', 0, 0, 210, 40);
      } catch (e) {
        doc.setFontSize(18);
        doc.text('Client Data Export', 14, 22);
      }
    } else {
      doc.setFontSize(18);
      doc.text('Client Data Export', 14, 22);
    }
    
    const startY = letterheadUrl ? 50 : 30;

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, startY);
    
    // Prepare table data
    const tableData = clients.map(client => [
      client.name,
      client.contactPerson || 'N/A',
      client.email || 'N/A',
      client.phone || 'N/A',
      client.address || 'N/A'
    ]);
    
    autoTable(doc, {
      startY: startY + 10,
      head: [['Client Name', 'Contact Person', 'Email', 'Phone', 'Address']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 138] }, // blue-900
      styles: { fontSize: 9, cellPadding: 3 },
    });
    
    downloadFile(doc.output('blob'), 'client-data.pdf').then(success => {
      if (success) toast.success('PDF exported successfully!');
    });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         client.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesService = !serviceFilter || (client.serviceTypes && client.serviceTypes.includes(serviceFilter));
    return matchesSearch && matchesService;
  });

  if (loading) return <div className="flex items-center justify-center h-64">Loading clients...</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Client Management</h1>
          <p className="text-slate-500">Manage customer records and contact information</p>
        </div>
        <div className="flex gap-4">
          <input
            type="file"
            id="csv-clients-import"
            className="hidden"
            accept=".csv"
            onChange={handleImportClientsCSV}
          />
          <button
            onClick={() => document.getElementById('csv-clients-import')?.click()}
            className="flex items-center justify-center gap-2 bg-white text-emerald-700 border border-emerald-200 px-6 py-3 rounded-2xl font-bold hover:bg-emerald-50 transition-all shadow-sm cursor-pointer"
          >
            <FileUp className="w-5 h-5" />
            Import CSV
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-200 px-6 py-3 rounded-2xl font-bold hover:bg-slate-50 transition-all shadow-sm cursor-pointer"
          >
            <Download className="w-5 h-5" />
            Export PDF (Raw)
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 bg-blue-50 text-blue-800 border border-blue-100 px-6 py-3 rounded-2xl font-bold hover:bg-blue-100 transition-all shadow-sm cursor-pointer"
          >
            <Printer className="w-5 h-5" />
            Bulk Export to PDF
          </button>
          <button
            onClick={() => openModal()}
            className="flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            Add Client
          </button>
        </div>
      </div>

      <div className="no-print space-y-8">
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={{ service: serviceFilter }}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <motion.div
              key={client.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="group bg-white border border-slate-100 rounded-3xl p-6 hover:shadow-xl hover:shadow-slate-100 transition-all relative overflow-hidden"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800 group-hover:bg-blue-800 group-hover:text-white transition-colors shadow-sm">
                  <Building2 className="w-6 h-6" />
                </div>
                <div className="relative">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenu(activeMenu === client.id ? null : client.id);
                    }}
                    className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-all"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  <AnimatePresence>
                    {activeMenu === client.id && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenu(null);
                          }} 
                        />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          className="absolute right-0 mt-2 w-48 bg-white border border-slate-100 rounded-2xl shadow-xl z-20 py-2"
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal(client);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                            Edit Client
                          </button>
                          {(isAdmin || isManager) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClient(client.id);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete Client
                            </button>
                          )}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <h3 className="text-lg font-bold text-slate-900 mb-1">{client.name}</h3>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {client.serviceTypes && client.serviceTypes.map((type, idx) => (
                  <span key={`${type}-${idx}`} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                    {type}
                  </span>
                ))}
                {(!client.serviceTypes || client.serviceTypes.length === 0) && (
                   <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                     Not Specified
                   </span>
                )}
                {getContractStatusBadge(client.contractEndDate)}
              </div>
              <p className="text-sm text-slate-500 mb-4">{client.contactPerson || 'No contact person'}</p>

              <div className="space-y-2 mb-6">
                {client.email && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Mail className="w-3.5 h-3.5" />
                    <span>{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Phone className="w-3.5 h-3.5" />
                    <span>{client.phone}</span>
                  </div>
                )}
              </div>

              <button 
                onClick={() => setViewingClient(client)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-blue-50 text-slate-600 hover:text-blue-800 rounded-xl transition-all group/btn"
              >
                <span className="text-xs font-black uppercase tracking-widest">View Details</span>
                <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Dedicated Print-Only Layout */}
      <div className="print-only">
        <div className="print-header">
          <h1 className="print-header-title">BRIGHT TECHNICAL SYSTEMS</h1>
          <p className="print-header-meta">
            <strong>Document Title:</strong> Filtered Clients Directory & Contact Information <br />
            <strong>Generated By:</strong> {profile?.displayName || 'Authorized User'} ({profile?.email || 'N/A'}) <br />
            <strong>Date Generated:</strong> {new Date().toLocaleString()} <br />
            <strong>Filters Applied:</strong> {serviceFilter ? `Service Type: ${serviceFilter}` : 'None'} {searchTerm ? `| Search Term: "${searchTerm}"` : ''}
          </p>
        </div>

        <div className="mt-4">
          <h2 className="text-base font-bold uppercase tracking-widest text-slate-800 mb-4 border-b pb-2">Clients Directory ({filteredClients.length} Records)</h2>
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="font-bold">Client Name</th>
                <th className="font-bold">Contact Person</th>
                <th className="font-bold">Email</th>
                <th className="font-bold">Phone</th>
                <th className="font-bold">Address</th>
                <th className="font-bold">Service Type</th>
                <th className="font-bold">Contract Period</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id}>
                  <td className="font-bold text-slate-900">{client.name}</td>
                  <td>{client.contactPerson || 'N/A'}</td>
                  <td>{client.email || 'N/A'}</td>
                  <td>{client.phone || 'N/A'}</td>
                  <td>{client.address || 'N/A'}</td>
                  <td>{client.serviceTypes && client.serviceTypes.join(', ') || 'N/A'}</td>
                  <td>
                    {client.contractCommencementDate && client.contractEndDate 
                      ? `${client.contractCommencementDate} to ${client.contractEndDate}`
                      : 'No Active Contract'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Details Modal */}
      <AnimatePresence>
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel="YES"
          cancelLabel="NO"
          isDanger={confirmModal.isDanger}
        />
        {viewingClient && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setViewingClient(null);
                setSelectedSpareParts([]);
              }}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[40px] shadow-2xl p-10 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-900 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/20">
                    <Building2 className="text-white w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-blue-900 tracking-tight">{viewingClient.name}</h2>
                    <p className="text-sm text-slate-500 font-medium">{viewingClient.contactPerson || 'No contact person'}</p>
                  </div>
                </div>
                <button onClick={() => {
                  setViewingClient(null);
                  setActiveTab('overview');
                  setSelectedSpareParts([]);
                }} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              {/* Tabs Navigation */}
              <div className="flex items-center gap-2 mb-8 p-1.5 bg-slate-100 rounded-2xl overflow-x-auto no-scrollbar">
                {[
                  { id: 'overview', label: 'Overview', icon: Building2 },
                  { id: 'tasks', label: 'Tasks', icon: ListTodo, count: tasks.length },
                  { id: 'reports', label: 'Reports', icon: FileText, count: reports.length },
                  { id: 'branches', label: 'Branches', icon: MapPin, count: branches.length },
                  { id: 'spare-parts', label: 'Spare Parts', icon: Package, count: viewingClient.spareParts?.length || 0 },
                  ...(viewingClient?.serviceType?.toLowerCase().includes('service') ? [
                    { id: 'general-service', label: 'General Service', icon: FileText, count: viewingClient.generalServiceItems?.length || 0 }
                  ] : []),
                  { id: 'contract-history', label: 'Contract History', icon: Clock, count: contractHistory.length },
                  { id: 'activity', label: 'Activity Log', icon: Activity, count: contractHistory.length + notes.length + tasks.length + reports.length },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={cn(
                      "flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap",
                      activeTab === tab.id
                        ? "bg-white text-blue-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                    {tab.count !== undefined && (
                      <span className={cn(
                        "ml-1 px-1.5 py-0.5 rounded-md text-[10px]",
                        activeTab === tab.id ? "bg-blue-50 text-blue-900" : "bg-slate-200 text-slate-500"
                      )}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="space-y-10">
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="space-y-8">
                      <section className="space-y-4">
                        <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Contact Information</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <Mail className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</p>
                              <p className="text-sm font-bold text-slate-700">{viewingClient.email || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <Phone className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone Number</p>
                              <p className="text-sm font-bold text-slate-700">{viewingClient.phone || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <MapPin className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Physical Address</p>
                              <p className="text-sm font-bold text-slate-700">{viewingClient.address || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>

                    <div className="space-y-8">
                      <section className="space-y-4">
                        <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Service Details</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Service Type</p>
                            <p className="text-sm font-bold text-slate-700">{viewingClient.serviceType || 'N/A'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Schedule</p>
                            <p className="text-sm font-bold text-slate-700">{viewingClient.schedule || 'N/A'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contract</p>
                            <p className="text-sm font-bold text-slate-700">{viewingClient.contractDuration || 'N/A'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Currency</p>
                            <p className="text-sm font-bold text-slate-700">{viewingClient.currency || defaultCurrency}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contract Commencement</p>
                            <p className="text-sm font-bold text-slate-700">{viewingClient.contractCommencementDate || 'N/A'}</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contract End Date</p>
                            <p className="text-sm font-bold text-slate-700">{viewingClient.contractEndDate || 'N/A'}</p>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                )}

                {activeTab === 'tasks' && (
                  <section className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Associated Tasks</h4>
                      <div className="flex gap-3">
                        <button 
                          onClick={() => navigate(`/tasks?clientId=${viewingClient.id}`)}
                          className="flex items-center gap-2 text-xs font-bold text-blue-800 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Manage in Tasks
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {tasks.length > 0 ? tasks.map(task => (
                        <div key={task.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 hover:border-blue-200 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-700">{task.title}</span>
                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${
                              task.status === 'completed' ? 'bg-green-100 text-green-700' : 
                              task.status === 'in-progress' ? 'bg-blue-100 text-blue-700' : 
                              'bg-amber-100 text-amber-700'
                            }`}>{task.status}</span>
                          </div>
                          {task.description && (
                            <p className="text-xs text-slate-500 line-clamp-2">{task.description}</p>
                          )}
                          <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium pt-2 border-t border-slate-200/50">
                            <div className="flex items-center gap-1">
                              <UserIcon className="w-3 h-3" />
                              <span>{task.technicianName || 'Unassigned'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{task.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="md:col-span-2 text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl">
                          <ListTodo className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                          <p className="text-sm font-bold text-slate-400">No tasks found for this client</p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {activeTab === 'reports' && (
                  <section className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Associated Reports</h4>
                      <button 
                        onClick={() => navigate(`/reports?clientId=${viewingClient.id}`)}
                        className="flex items-center gap-2 text-xs font-bold text-blue-800 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Manage in Reports
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {reports.length > 0 ? reports.map(report => (
                        <div key={report.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3 hover:border-blue-200 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-700">{report.type.toUpperCase()} Report</span>
                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${
                              report.status === 'approved' ? 'bg-green-100 text-green-700' : 
                              report.status === 'rejected' ? 'bg-red-100 text-red-700' : 
                              'bg-blue-100 text-blue-700'
                            }`}>{report.status}</span>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium pt-2 border-t border-slate-200/50">
                            <div className="flex items-center gap-1">
                              <UserIcon className="w-3 h-3" />
                              <span>{report.technicianName}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="md:col-span-2 text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl">
                          <FileText className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                          <p className="text-sm font-bold text-slate-400">No reports found for this client</p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {activeTab === 'branches' && (
                  <section className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Branches</h4>
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                          <input
                            type="text"
                            placeholder="Search branches..."
                            value={branchSearch}
                            onChange={(e) => setBranchSearch(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all w-48 md:w-64"
                          />
                        </div>
                        <button
                          onClick={handleDownloadBranchTemplate}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-100 transition-all shadow-xs"
                          title="Download Excel Template for Branches"
                        >
                          <Download className="w-3.5 h-3.5 text-blue-600" />
                          <span>Template</span>
                        </button>
                        <label className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold hover:bg-emerald-100 transition-all cursor-pointer shadow-xs">
                          <FileUp className="w-3.5 h-3.5 text-emerald-700" />
                          <span>Import Excel</span>
                          <input type="file" accept=".xlsx, .xls, .csv" onChange={handleImportBranchesExcel} className="hidden" />
                        </label>
                        <button onClick={() => setIsBranchModalOpen(true)} className="px-3 py-1.5 bg-blue-900 text-white rounded-xl text-xs font-bold hover:bg-blue-800 transition-all flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" />
                          <span>Add Branch</span>
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {branches.filter(b => 
                        b.name.toLowerCase().includes(branchSearch.toLowerCase()) || 
                        (b.address?.toLowerCase().includes(branchSearch.toLowerCase()) ?? false)
                      ).length > 0 ? branches.filter(b => 
                        b.name.toLowerCase().includes(branchSearch.toLowerCase()) || 
                        (b.address?.toLowerCase().includes(branchSearch.toLowerCase()) ?? false)
                      ).map(branch => (
                        <div key={branch.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-slate-100 shadow-xs">
                              <MapPin className="w-5 h-5 text-blue-900" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">{branch.name}</p>
                              <p className="text-[11px] text-slate-500 font-medium">{branch.address || 'No address provided'}</p>
                              {typeof branch.acQuantity === 'number' && (
                                <span className="inline-block mt-1 px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-lg text-[10px] font-black uppercase tracking-wider">
                                  AC Quantity: {branch.acQuantity}
                                </span>
                              )}
                            </div>
                          </div>
                          {(isAdmin || isManager) && (
                            <button
                              onClick={() => deleteBranch(branch.id)}
                              className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title="Delete Branch"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )) : (
                        <div className="md:col-span-2 text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl">
                          <MapPin className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                          <p className="text-sm font-bold text-slate-400">No branches added yet</p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {activeTab === 'spare-parts' && (
                  <section className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Spare Parts & Pricing</h4>
                      <div className="flex flex-wrap items-center gap-4">
                        {selectedSpareParts.length > 0 && (isAdmin || isManager) && (
                          <button
                            onClick={deleteSelectedSpareParts}
                            className="flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-100 transition-all shadow-sm"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete Selected ({selectedSpareParts.length})
                          </button>
                        )}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                          <input
                            type="text"
                            placeholder="Search spare parts..."
                            value={sparePartSearch}
                            onChange={(e) => setSparePartSearch(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all w-48"
                          />
                        </div>
                        <input
                          type="file"
                          id="excel-import"
                          className="hidden"
                          accept=".xlsx, .xls, .csv"
                          onChange={handleImportExcel}
                        />
                        <button 
                          onClick={() => {
                            const headers = ['Name', 'Unit', 'Price'];
                            const data = [['Example Part', 'pcs', '10000']];
                            const ws = utils.aoa_to_sheet([headers, ...data]);
                            const wb = utils.book_new();
                            utils.book_append_sheet(wb, ws, 'Template');
                            const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
                            const blob = new Blob([wbout], { type: 'application/octet-stream' });
                            downloadFile(blob, 'spare_parts_template.xlsx');
                          }}
                          className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          Download Template
                        </button>
                        <button 
                          onClick={() => document.getElementById('excel-import')?.click()} 
                          className="text-xs font-bold text-emerald-600 hover:underline flex items-center gap-1"
                        >
                          <FileUp className="w-3 h-3" />
                          Import from Excel
                        </button>
                        <button onClick={() => {
                          setEditingPartIndex(null);
                          setNewPart({ name: '', price: '', unit: '' });
                          setIsSparePartsModalOpen(true);
                        }} className="text-xs font-bold text-blue-800 hover:underline">Add Spare Parts Price</button>
                      </div>
                    </div>
                    <div className="border border-slate-100 rounded-3xl overflow-hidden">
                      <table className="w-full text-left">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-6 py-4 w-10">
                              <input
                                type="checkbox"
                                checked={selectedSpareParts.length === (viewingClient.spareParts || []).length && (viewingClient.spareParts || []).length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSpareParts((viewingClient.spareParts || []).map((_, i) => i));
                                  } else {
                                    setSelectedSpareParts([]);
                                  }
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-blue-800 focus:ring-blue-800/20 cursor-pointer"
                              />
                            </th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Part Name</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Unit</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Price</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                           {(viewingClient.spareParts || [])
                            .map((part, originalIndex) => ({ ...part, originalIndex }))
                            .filter(p => p.name.toLowerCase().includes(sparePartSearch.toLowerCase()))
                            .length > 0 ? (viewingClient.spareParts || [])
                            .map((part, originalIndex) => ({ ...part, originalIndex }))
                            .filter(p => p.name.toLowerCase().includes(sparePartSearch.toLowerCase()))
                            .map((part) => (
                            <tr key={part.originalIndex} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-6 py-4">
                                <input
                                  type="checkbox"
                                  checked={selectedSpareParts.includes(part.originalIndex)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedSpareParts(prev => [...prev, part.originalIndex]);
                                    } else {
                                      setSelectedSpareParts(prev => prev.filter(item => item !== part.originalIndex));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-slate-300 text-blue-800 focus:ring-blue-800/20 cursor-pointer"
                                />
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <Package className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm font-bold text-slate-700">{part.name}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg uppercase tracking-wider">{part.unit || '-'}</span>
                              </td>
                              <td className="px-6 py-4 text-sm font-black text-blue-900 text-right">{viewingClient.currency || defaultCurrency} {part.price.toLocaleString()}</td>
                              <td className="px-6 py-4 text-right">
                                {(isAdmin || isManager) && (
                                  <div className="flex items-center justify-end gap-2">
                                    <button onClick={() => {
                                      setEditingPartIndex(part.originalIndex);
                                      setNewPart({ name: part.name, price: part.price.toString(), unit: part.unit || '' });
                                      setIsSparePartsModalOpen(true);
                                    }} className="p-2 text-slate-400 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all" title="Edit Part">
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => deleteSparePart(part.originalIndex)} className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Delete Part">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan={5} className="px-6 py-12 text-center">
                                <Package className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                                <p className="text-sm font-bold text-slate-400">No spare parts listed</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {activeTab === 'general-service' && (
                  <section className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">General Service Prices</h4>
                    </div>
                    <div className="border border-slate-100 rounded-3xl overflow-hidden">
                      <table className="w-full text-left">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">S/N</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Description</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">QTY</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Unit Price</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Total Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50 bg-white">
                          {(viewingClient.generalServiceItems || []).map((item, index) => (
                            <tr key={`${index}-${item.description}`} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-6 py-4 text-xs font-bold text-slate-500">{index + 1}</td>
                              <td className="px-6 py-4 text-xs font-bold text-slate-900">{item.description}</td>
                              <td className="px-6 py-4 text-xs font-bold text-slate-600 text-center">{item.quantity}</td>
                              <td className="px-6 py-4 text-xs font-bold text-slate-900 text-right">
                                {viewingClient.currency || 'TZS'} {item.unitPrice.toLocaleString()}
                              </td>
                              <td className="px-6 py-4 text-xs font-black text-blue-900 text-right">
                                {viewingClient.currency || 'TZS'} {(item.quantity * item.unitPrice).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                          {(viewingClient.generalServiceItems || []).length === 0 && (
                            <tr>
                              <td colSpan={5} className="px-6 py-10 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">
                                No general service items configured. Click 'Edit Client' to add them.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {activeTab === 'contract-history' && (
                  <section className="space-y-6 animate-fadeIn">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Contract Lifecycle History</h4>
                      <div className="relative max-w-xs w-full">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search by date, ref, or type..."
                          value={contractHistorySearch}
                          onChange={(e) => setContractHistorySearch(e.target.value)}
                          className="pl-9 pr-8 py-2 w-full text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 placeholder:text-slate-400"
                        />
                        {contractHistorySearch && (
                          <button
                            onClick={() => setContractHistorySearch('')}
                            className="absolute right-3 top-2.5 text-[9px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wider"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {(() => {
                      const filteredHistory = contractHistory.filter((item, index) => {
                        if (!contractHistorySearch) return true;
                        const searchLower = contractHistorySearch.toLowerCase();
                        const referenceNum = item.id ? item.id.substring(0, 8) : `REV-${index + 1}`;
                        const refId = referenceNum.toLowerCase();
                        const commencement = (item.contractCommencementDate || '').toLowerCase();
                        const endDate = (item.contractEndDate || '').toLowerCase();
                        const duration = (item.contractDuration || '').toLowerCase();
                        const serviceType = (item.serviceType || '').toLowerCase();
                        const changedBy = (item.changedBy || '').toLowerCase();
                        
                        let changedAtStr = '';
                        if (item.changedAt && typeof item.changedAt.toDate === 'function') {
                          changedAtStr = item.changedAt.toDate().toLocaleString().toLowerCase();
                        }
                        
                        return (
                          refId.includes(searchLower) ||
                          commencement.includes(searchLower) ||
                          endDate.includes(searchLower) ||
                          duration.includes(searchLower) ||
                          serviceType.includes(searchLower) ||
                          changedBy.includes(searchLower) ||
                          changedAtStr.includes(searchLower)
                        );
                      });

                      return (
                        <div className="relative border-l-2 border-slate-100 pl-6 ml-3 space-y-8 mt-4">
                          {filteredHistory.length > 0 ? (
                            filteredHistory.map((item, index) => {
                              const referenceNum = item.id ? item.id.substring(0, 8).toUpperCase() : `REV-${index + 1}`;
                              return (
                                <div key={item.id || index} className="relative">
                                  <span className="absolute -left-[31px] top-1 bg-white border-4 border-blue-900 rounded-full w-4 h-4" />
                                  <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl space-y-3">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="px-2.5 py-1 bg-blue-50 text-blue-800 text-[10px] font-black uppercase tracking-wider rounded-lg">
                                          Version Audit Log
                                        </span>
                                        <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md font-bold">
                                          Ref: #{referenceNum}
                                        </span>
                                      </div>
                                      <span className="text-[10px] font-bold text-slate-400">
                                        Changed on {item.changedAt?.toDate?.()?.toLocaleString() || 'N/A'} by {item.changedBy || 'System'}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs pt-1">
                                      <div>
                                        <p className="font-bold text-slate-400 uppercase text-[9px] tracking-wider">Commencement</p>
                                        <p className="font-bold text-slate-700 mt-0.5">{item.contractCommencementDate || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="font-bold text-slate-400 uppercase text-[9px] tracking-wider">Expiry Date</p>
                                        <p className="font-bold text-slate-700 mt-0.5">{item.contractEndDate || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="font-bold text-slate-400 uppercase text-[9px] tracking-wider">Duration</p>
                                        <p className="font-bold text-slate-700 mt-0.5">{item.contractDuration || 'N/A'}</p>
                                      </div>
                                      <div>
                                        <p className="font-bold text-slate-400 uppercase text-[9px] tracking-wider">Service Type</p>
                                        <p className="font-bold text-slate-700 mt-0.5">{item.serviceType || 'N/A'}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl -ml-6">
                              <Clock className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                              <p className="text-sm font-bold text-slate-400">
                                {contractHistorySearch ? "No contract modifications match your search criteria." : "No previous contract modifications recorded."}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {contractHistorySearch ? "Try adjusting your search terms or clearing the filter." : "Changes to commencement or end dates will generate a version history log."}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </section>
                )}

                {activeTab === 'activity' && (
                  <section className="space-y-6 animate-fadeIn">
                    <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-4">Unified Activity Audit Trail</h4>
                    
                    {/* Log custom note form */}
                    <form onSubmit={handleAddNote} className="bg-slate-50 border border-slate-100 p-5 rounded-3xl space-y-3">
                      <label className="text-xs font-black text-slate-700 block uppercase tracking-wider">
                        Log a Custom Activity Note
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Type a note about this client (e.g. Note about site readiness)..."
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          className="flex-1 px-4 py-3 text-xs bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 placeholder:text-slate-400"
                        />
                        <button
                          type="submit"
                          disabled={isAddingNote || !newNoteText.trim()}
                          className="bg-blue-800 text-white text-xs font-black px-5 py-3 rounded-xl hover:bg-blue-900 transition-all disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                        >
                          {isAddingNote ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                          Log Note
                        </button>
                      </div>
                    </form>

                    {/* Timeline */}
                    <div className="relative border-l-2 border-slate-100 pl-6 ml-3 space-y-6 mt-6">
                      {(() => {
                        // Gather and normalize activities
                        const allActivities: any[] = [];

                        // 1. Contract History
                        contractHistory.forEach((ch, idx) => {
                          allActivities.push({
                            id: ch.id || `ch-${idx}`,
                            type: 'contract',
                            title: 'Contract Updated',
                            description: `Contract details amended by ${ch.changedBy || 'System'}.`,
                            meta: `Commencement: ${ch.contractCommencementDate || 'N/A'} | Expiry: ${ch.contractEndDate || 'N/A'} | Duration: ${ch.contractDuration || 'N/A'}`,
                            timestamp: ch.changedAt,
                            badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-100',
                            icon: Clock,
                            iconColor: 'text-indigo-600',
                            user: ch.changedBy || 'System'
                          });
                        });

                        // 2. Custom Notes
                        notes.forEach((nt) => {
                          allActivities.push({
                            id: nt.id,
                            type: 'note',
                            title: 'Note Logged',
                            description: nt.text,
                            meta: nt.createdByEmail ? `Logged by ${nt.createdBy} (${nt.createdByEmail})` : `Logged by ${nt.createdBy}`,
                            timestamp: nt.createdAt,
                            badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-100',
                            icon: FileText,
                            iconColor: 'text-emerald-600',
                            user: nt.createdBy || 'System'
                          });
                        });

                        // 3. Tasks / Service Requests
                        tasks.forEach((tk) => {
                          allActivities.push({
                            id: tk.id,
                            type: 'task',
                            title: `Service Request Created`,
                            description: tk.title,
                            meta: `Type: ${tk.type || 'General'} | Status: ${tk.status.toUpperCase()} | Assigned to: ${tk.technicianName || 'Unassigned'}`,
                            timestamp: tk.createdAt,
                            badgeColor: 'bg-amber-50 text-amber-700 border-amber-100',
                            icon: ListTodo,
                            iconColor: 'text-amber-600',
                            user: 'System'
                          });
                        });

                        // 4. Reports
                        reports.forEach((rep) => {
                          allActivities.push({
                            id: rep.id,
                            type: 'report',
                            title: `Technical Report Submitted`,
                            description: `Report filed for client. Status: ${rep.status.replace(/_/g, ' ').toUpperCase()}`,
                            meta: `Technician: ${rep.technicianName || 'N/A'} | Type: ${rep.type.toUpperCase()}`,
                            timestamp: rep.createdAt,
                            badgeColor: 'bg-rose-50 text-rose-700 border-rose-100',
                            icon: CheckCircle2,
                            iconColor: 'text-rose-600',
                            user: rep.technicianName || 'System'
                          });
                        });

                        // Sort chronological descending
                        allActivities.sort((a, b) => {
                          const tA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0;
                          const tB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0;
                          return tB - tA;
                        });

                        if (allActivities.length === 0) {
                          return (
                            <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-3xl -ml-6">
                              <Activity className="w-12 h-12 text-slate-200 mx-auto mb-3 animate-pulse" />
                              <p className="text-sm font-bold text-slate-400">No activity recorded yet.</p>
                              <p className="text-xs text-slate-400 mt-1">Activities are automatically logged when contracts are updated, reports are submitted, tasks are created, or notes are added.</p>
                            </div>
                          );
                        }

                        return allActivities.map((act, idx) => {
                          const formattedDate = act.timestamp?.toDate 
                            ? act.timestamp.toDate().toLocaleString() 
                            : 'N/A';

                          return (
                            <div key={`${act.type}-${act.id || idx}`} className="relative animate-fadeIn">
                              <span className="absolute -left-[31px] top-1 bg-white border-4 border-slate-100 rounded-full w-4 h-4 flex items-center justify-center">
                                <span className={cn("w-1.5 h-1.5 rounded-full", act.type === 'note' ? 'bg-emerald-500' : act.type === 'contract' ? 'bg-indigo-500' : act.type === 'task' ? 'bg-amber-500' : 'bg-rose-500')} />
                              </span>
                              <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl space-y-2">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className={cn("px-2 py-0.5 border text-[9px] font-black uppercase tracking-wider rounded-md", act.badgeColor)}>
                                      {act.title}
                                    </span>
                                    <span className="text-[10px] font-bold text-slate-400">
                                      {formattedDate}
                                    </span>
                                  </div>
                                  <span className="text-[10px] font-bold text-slate-500 bg-white border border-slate-100 px-2 py-0.5 rounded-md">
                                    by {act.user}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-800 font-bold leading-relaxed">{act.description}</p>
                                {act.meta && (
                                  <p className="text-[10px] text-slate-500 font-medium bg-white/50 p-2 rounded-lg border border-slate-100/50 mt-1">{act.meta}</p>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </section>
                )}
              </div>

              <div className="mt-10 pt-8 border-t border-slate-50 flex justify-end gap-4">
                {(isAdmin || isManager) && (
                  <button 
                    onClick={() => {
                      if (viewingClient) {
                         handleDeleteClient(viewingClient.id);
                      }
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-600 rounded-2xl font-bold hover:bg-red-100 transition-all border border-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Client Profile
                  </button>
                )}
                <button 
                  onClick={() => {
                    setViewingClient(null);
                    openModal(viewingClient);
                  }}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Client Data
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Branch Modal */}
        {isBranchModalOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsBranchModalOpen(false)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8"
            >
              <h2 className="text-xl font-black text-blue-900 mb-6">Add New Branch</h2>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Branch Name (e.g. Arusha Main Branch)"
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                />
                <input
                  type="text"
                  placeholder="Branch Address / Location"
                  value={branchAddress}
                  onChange={(e) => setBranchAddress(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                />
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Number of ACs (AC Quantity)</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="Number of Air Conditioners served"
                    value={branchAcQuantity}
                    onChange={(e) => setBranchAcQuantity(e.target.value ? Number(e.target.value) : '')}
                    className="w-full px-4 py-3 bg-blue-50 border border-blue-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/20 transition-all font-bold text-blue-900"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setIsBranchModalOpen(false)}
                    className="flex-1 px-6 py-3 border border-slate-200 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addBranch}
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Add Branch'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Add Spare Parts Price Modal */}
        {isSparePartsModalOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSparePartsModalOpen(false)}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8"
            >
              <h2 className="text-xl font-black text-blue-900 mb-6">{editingPartIndex !== null ? 'Edit Spare Part Price' : 'Add Spare Part Price'}</h2>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Part Name"
                  value={newPart.name}
                  onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                />
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    placeholder="Unit (e.g. pcs, box, kg)"
                    value={newPart.unit}
                    onChange={(e) => setNewPart({ ...newPart, unit: e.target.value })}
                    className="w-full sm:flex-1 min-w-0 px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={newPart.price}
                    onChange={(e) => setNewPart({ ...newPart, price: e.target.value })}
                    className="w-full sm:flex-1 min-w-0 px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => setIsSparePartsModalOpen(false)}
                    className="flex-1 px-6 py-3 border border-slate-200 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveSparePartPrice}
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingPartIndex !== null ? 'Update Price' : 'Add Price')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Client Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-blue-900/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-3xl bg-white rounded-[40px] shadow-2xl p-10 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-black text-blue-900 tracking-tight">
                  {editingClient ? 'Edit Client Data' : 'Add New Client'}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="space-y-12">
                  {/* Basic Information */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-blue-600" />
                      </div>
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Basic Information</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client Name *</label>
                        <input
                          required
                          type="text"
                          value={clientFormData.name}
                          onChange={(e) => setFormData({ ...clientFormData, name: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          placeholder="Company or Individual Name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Physical Address</label>
                        <textarea
                          rows={2}
                          value={clientFormData.address}
                          onChange={(e) => setFormData({ ...clientFormData, address: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all resize-none font-bold"
                          placeholder="Full office or site address"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Contact Details */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <Phone className="w-4 h-4 text-blue-600" />
                      </div>
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Contact Details</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Person</label>
                        <input
                          type="text"
                          value={clientFormData.contactPerson}
                          onChange={(e) => setFormData({ ...clientFormData, contactPerson: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          placeholder="Full Name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Phone Number</label>
                        <input
                          type="tel"
                          value={clientFormData.phone}
                          onChange={(e) => setFormData({ ...clientFormData, phone: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          placeholder="+255..."
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email Address</label>
                        <input
                          type="email"
                          value={clientFormData.email}
                          onChange={(e) => setFormData({ ...clientFormData, email: e.target.value })}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                          placeholder="client@example.com"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Branches Section */}
                  <section className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-indigo-600" />
                        </div>
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Client Branches</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleDownloadBranchTemplate}
                          className="flex items-center gap-1 text-[10px] font-black text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-blue-200 hover:bg-blue-100 transition-all"
                          title="Download Excel Template"
                        >
                          <Download className="w-3 h-3" />
                          <span>Template</span>
                        </button>
                        <label className="flex items-center gap-1 text-[10px] font-black text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg uppercase tracking-widest border border-emerald-200 hover:bg-emerald-100 transition-all cursor-pointer">
                          <FileUp className="w-3 h-3" />
                          <span>Import Excel</span>
                          <input type="file" accept=".xlsx, .xls, .csv" onChange={handleImportBranchesExcel} className="hidden" />
                        </label>
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...clientFormData,
                            branches: [...(clientFormData.branches || []), { name: '', address: '', acQuantity: 0 }]
                          })}
                          className="flex items-center gap-1 text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:text-indigo-700"
                        >
                          <Plus className="w-3 h-3" /> Add Branch
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {clientFormData.branches && clientFormData.branches.length > 0 ? (
                        clientFormData.branches.map((branch, index) => (
                          <div key={`${index}-${branch.name}`} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-2xl relative group">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Branch Name</label>
                              <input
                                type="text"
                                value={branch.name}
                                onChange={(e) => {
                                  const newBranches = [...(clientFormData.branches || [])];
                                  newBranches[index].name = e.target.value;
                                  setFormData({ ...clientFormData, branches: newBranches });
                                }}
                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-800/10 text-xs font-bold"
                                placeholder="e.g. Arusha Branch"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Branch Address</label>
                              <input
                                type="text"
                                value={branch.address}
                                onChange={(e) => {
                                  const newBranches = [...(clientFormData.branches || [])];
                                  newBranches[index].address = e.target.value;
                                  setFormData({ ...clientFormData, branches: newBranches });
                                }}
                                className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-800/10 text-xs font-bold"
                                placeholder="Branch location address"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">AC Quantity</label>
                              <div className="flex gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  value={branch.acQuantity ?? ''}
                                  onChange={(e) => {
                                    const newBranches = [...(clientFormData.branches || [])];
                                    newBranches[index].acQuantity = Number(e.target.value) || 0;
                                    setFormData({ ...clientFormData, branches: newBranches });
                                  }}
                                  className="flex-1 px-4 py-2 bg-white border border-indigo-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-800/10 text-xs font-bold text-indigo-900"
                                  placeholder="0"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newBranches = clientFormData.branches?.filter((_, i) => i !== index);
                                    setFormData({ ...clientFormData, branches: newBranches });
                                  }}
                                  className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 border-2 border-dashed border-slate-100 rounded-2xl">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No branches added yet</p>
                        </div>
                      )}
                    </div>
                  </section>

                  {/* Service Information */}
                  <section className="space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-blue-600" />
                      </div>
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">Service Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Services Offered (Select one or more) *</label>
                          <div className="grid grid-cols-1 gap-2.5 p-4 bg-slate-50 border border-slate-100 rounded-2xl max-h-48 overflow-y-auto">
                            {allServiceTypes.map((type, idx) => {
                              const selectedList = clientFormData.serviceTypes || [];
                              const isChecked = selectedList.includes(type.name);
                              return (
                                <label key={type.id || `st-${idx}`} className="flex items-center gap-3 p-2 hover:bg-slate-100/50 rounded-xl cursor-pointer transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      let newList: string[];
                                      if (e.target.checked) {
                                        newList = [...selectedList, type.name];
                                      } else {
                                        newList = selectedList.filter((s: string) => s !== type.name);
                                      }
                                      setFormData({ ...clientFormData, serviceTypes: newList });
                                    }}
                                    className="w-4 h-4 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                                  />
                                  <span className="text-xs font-bold text-slate-700">{type.name}</span>
                                </label>
                              );
                            })}
                            {allServiceTypes.length === 0 && (
                              <p className="text-xs text-slate-400 italic">No registered service types found.</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Schedule</label>
                            <input
                              type="text"
                              value={clientFormData.schedule}
                              onChange={(e) => setFormData({ ...clientFormData, schedule: e.target.value })}
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              placeholder="e.g. Monthly"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Contract Duration</label>
                            <input
                              type="text"
                              value={clientFormData.contractDuration}
                              onChange={(e) => setFormData({ ...clientFormData, contractDuration: e.target.value })}
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                              placeholder="e.g. 12 Months"
                            />
                          </div>
                        </div>
                        <div className="bg-slate-50 border border-slate-100 rounded-[28px] p-5 space-y-4">
                          <span className="text-[10px] font-black text-blue-900 uppercase tracking-widest flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-blue-800" />
                            Contract Validity Period
                          </span>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Contract Commencement</label>
                              <input
                                type="date"
                                value={clientFormData.contractCommencementDate}
                                onChange={(e) => setFormData({ ...clientFormData, contractCommencementDate: e.target.value })}
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold text-xs"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Contract End</label>
                              <input
                                type="date"
                                value={clientFormData.contractEndDate}
                                onChange={(e) => setFormData({ ...clientFormData, contractEndDate: e.target.value })}
                                className={cn(
                                  "w-full px-4 py-3 bg-white border rounded-2xl focus:outline-none focus:ring-2 transition-all font-bold text-xs",
                                  clientFormData.contractCommencementDate && clientFormData.contractEndDate && new Date(clientFormData.contractEndDate) < new Date(clientFormData.contractCommencementDate)
                                    ? "border-red-300 focus:ring-red-200 text-red-600"
                                    : "border-slate-200 focus:ring-blue-800/10 text-slate-700"
                                )}
                              />
                            </div>
                          </div>

                          {/* Quick Duration Shortcuts */}
                          {clientFormData.contractCommencementDate && (
                            <div className="pt-2">
                              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1.5">Quick Validity Term Shortcuts</span>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  { label: '+3 Months', val: 3 },
                                  { label: '+6 Months', val: 6 },
                                  { label: '+1 Year', val: 12 },
                                  { label: '+2 Years', val: 24 },
                                ].map((shortcut) => (
                                  <button
                                    key={shortcut.label}
                                    type="button"
                                    onClick={() => {
                                      const start = new Date(clientFormData.contractCommencementDate);
                                      start.setMonth(start.getMonth() + shortcut.val);
                                      start.setDate(start.getDate() - 1);
                                      const formatted = start.toISOString().split('T')[0];
                                      setFormData({
                                        ...clientFormData,
                                        contractEndDate: formatted,
                                        contractDuration: shortcut.label.replace('+', '')
                                      });
                                      toast.success(`Set expiry date to ${formatted} (${shortcut.label})`);
                                    }}
                                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-[10px] font-bold text-slate-600 hover:border-blue-800 hover:text-blue-800 transition-all cursor-pointer shadow-sm"
                                  >
                                    {shortcut.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Live validation feedback */}
                          {clientFormData.contractCommencementDate && clientFormData.contractEndDate && (
                            <div className="pt-1">
                              {new Date(clientFormData.contractEndDate) < new Date(clientFormData.contractCommencementDate) ? (
                                <div className="p-3 bg-red-50 text-red-700 text-xs font-bold rounded-xl flex items-center gap-2 border border-red-100">
                                  <span>⚠️ Error: Expiry date cannot be before Commencement date. Form submission is disabled.</span>
                                </div>
                              ) : (
                                <div className="p-3 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-xl flex items-center justify-between border border-emerald-100">
                                  <span>✅ Valid contract period</span>
                                  <span className="font-mono">
                                    {Math.ceil((new Date(clientFormData.contractEndDate).getTime() - new Date(clientFormData.contractCommencementDate).getTime()) / (1000 * 3600 * 24)) + 1} Days
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Currency</label>
                          <select
                            value={clientFormData.currency}
                            onChange={(e) => setFormData({ ...clientFormData, currency: e.target.value })}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold cursor-pointer"
                          >
                            <option value="TZS">TZS (Tanzanian Shilling)</option>
                            <option value="USD">USD (US Dollar)</option>
                            <option value="EUR">EUR (Euro)</option>
                            <option value="GBP">GBP (British Pound)</option>
                            <option value="KES">KES (Kenyan Shilling)</option>
                            <option value="UGX">UGX (Ugandan Shilling)</option>
                            <option value="ZAR">ZAR (South African Rand)</option>
                          </select>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Spare Parts & Pricing</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Part Name"
                            value={newPart.name}
                            onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                            className="flex-1 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 text-xs font-bold"
                          />
                          <input
                            type="text"
                            placeholder="Unit"
                            value={newPart.unit}
                            onChange={(e) => setNewPart({ ...newPart, unit: e.target.value })}
                            className="w-20 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 text-xs font-bold"
                          />
                          <input
                            type="number"
                            placeholder="Price"
                            value={newPart.price}
                            onChange={(e) => setNewPart({ ...newPart, price: e.target.value })}
                            className="w-24 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 text-xs font-bold"
                          />
                          <button
                            type="button"
                            onClick={addSparePart}
                            className="p-2 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-colors"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                          {spareParts.length > 0 ? spareParts.map((part, i) => (
                            <div key={`${i}-${part.name}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                              <div className="flex items-center gap-2">
                                <Package className="w-3.5 h-3.5 text-blue-400" />
                                <span className="text-xs font-bold text-slate-700">{part.name}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                {part.unit && <span className="text-[10px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded flex items-center h-fit">{part.unit}</span>}
                                <span className="text-xs font-black text-blue-900">${part.price.toFixed(2)}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSparePart(i)}
                                  className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                  title="Remove Part"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )) : (
                            <div className="text-center py-6 border-2 border-dashed border-slate-100 rounded-2xl">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No spare parts added</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {clientFormData.serviceTypes && clientFormData.serviceTypes.join(', ').toLowerCase().includes('service') && (
                        <div className="space-y-4 pt-6 border-t border-slate-200/50">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">General Service Pricing (Air Conditioners)</label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="Description of General Service"
                              value={newGeneralServiceItem.description}
                              onChange={(e) => setNewGeneralServiceItem({ ...newGeneralServiceItem, description: e.target.value })}
                              className="px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 text-xs font-bold"
                            />
                            <input
                              type="number"
                              placeholder="QTY"
                              value={newGeneralServiceItem.quantity}
                              onChange={(e) => setNewGeneralServiceItem({ ...newGeneralServiceItem, quantity: e.target.value })}
                              className="px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 text-xs font-bold"
                            />
                            <div className="flex gap-2">
                              <input
                                type="number"
                                placeholder="Unit Price"
                                value={newGeneralServiceItem.unitPrice}
                                onChange={(e) => setNewGeneralServiceItem({ ...newGeneralServiceItem, unitPrice: e.target.value })}
                                className="flex-1 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 text-xs font-bold"
                              />
                              <button
                                type="button"
                                onClick={addGeneralServiceItem}
                                className="p-2 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-colors"
                              >
                                <Plus className="w-5 h-5" />
                              </button>
                            </div>
                          </div>

                          <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                            {generalServiceItems.length > 0 ? generalServiceItems.map((item, i) => (
                              <div key={`${i}-${item.description}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded">{i + 1}</span>
                                  <span className="text-xs font-bold text-slate-700">{item.description}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-xs font-medium text-slate-500">Qty: {item.quantity}</span>
                                  <span className="text-xs font-black text-blue-900">{clientFormData.currency || 'TZS'} {item.unitPrice.toLocaleString()}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeGeneralServiceItem(i)}
                                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                    title="Remove Item"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )) : (
                              <div className="text-center py-6 border-2 border-dashed border-slate-100 rounded-2xl">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No general service prices configured</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                </div>

                <div className="flex gap-4 pt-8 border-t border-slate-50">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-4 border-2 border-slate-100 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !!(clientFormData.contractCommencementDate && clientFormData.contractEndDate && new Date(clientFormData.contractEndDate) < new Date(clientFormData.contractCommencementDate))}
                    className="flex-1 px-6 py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-xl shadow-blue-900/20 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingClient ? 'Update Client Data' : 'Create Client')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientsPage;
